import { AlertService } from '@anecdotes/design-system/alert';
import { TextAreaControl, TextFieldControl, ToggleBoxControl } from '@anecdotes/design-system/form-controls';
import { ModalWindowService } from '@anecdotes/design-system/modals';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { DynamicFormGroup } from '@anecdotes/dynamic-form';
import { Subject } from 'rxjs';
import { PluginService } from '@anecdotes/data-store';
import { TrustCenterFacadeService, SubprocessorResponse } from '@anecdotes/shared-trust-center';

enum FormControls {
  Location = 'location',
  Usage = 'usage',
  IsVisible = 'isVisible',
}

interface InitialValues {
  [FormControls.Location]: string;
  [FormControls.Usage]: string;
  [FormControls.IsVisible]: string;
}

@Component({
  selector: 'app-subprocessor-edit-side-panel',
  templateUrl: './subprocessor-edit-side-panel.component.html',
  styleUrls: ['./subprocessor-edit-side-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SubprocessorEditSidePanelComponent implements OnInit, OnDestroy {
  @HostBinding('class')
  private readonly classes = 'h-full';

  @ViewChild('contentContainer')
  private contentContainer: ElementRef<HTMLDivElement>;

  @ViewChild('nameContainer')
  private subprocessorNameContainer: ElementRef<HTMLDivElement>;

  private resizeObserver: ResizeObserver;

  readonly sendingRequestBtnLoader$ = new Subject<boolean>();

  private initialFormValues: InitialValues;

  @Input()
  subprocessor: SubprocessorResponse;

  serviceIcon: string;

  form: DynamicFormGroup<any>;

  formMaxHeight: number;

  get wasFormValueChanged(): boolean {
    if (!this.form) {
      return false;
    }

    return Object.values(FormControls).some((name) => this.form.items[name].value !== this.initialFormValues[name]);
  }

  constructor(
    private trustCenterFacadeService: TrustCenterFacadeService,
    private modalWindowService: ModalWindowService,
    private pluginService: PluginService,
    private alertService: AlertService,
    private elementRef: ElementRef,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.handleSidePanelResizing();
    this.initForm();
    this.serviceIcon = this.pluginService.getServiceIconLinkSync(this.subprocessor.id);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  buildTranslationKey(relativeKey: string): string {
    return `trustCenter.${relativeKey}`;
  }

  async saveBtnClick(): Promise<void> {
    if (!this.wasFormValueChanged) {
      return;
    }

    this.sendingRequestBtnLoader$.next(true);

    try {
      await this.trustCenterFacadeService.updateSubprocessor({
        subprocessor_id: this.subprocessor.id,
        location: this.form.controls[FormControls.Location].value,
        usage: this.form.controls[FormControls.Usage].value,
        is_visible: this.form.controls[FormControls.IsVisible].value,
      });
      this.closePanel();
    } catch {
      this.alertService.openActionErrorAlert({
        customBaseTranslationKey: this.buildTranslationKey('tryAgainError'),
      });
    } finally {
      this.sendingRequestBtnLoader$.next(false);
    }
  }

  closePanel(): void {
    this.modalWindowService.close();
  }

  private initForm(): void {
    this.form = new DynamicFormGroup({
      [FormControls.IsVisible]: new ToggleBoxControl({
        initialInputs: {
          title: 'Show',
        },
        initialValue: this.subprocessor.is_visible,
      }),

      [FormControls.Location]: new TextFieldControl({
        initialInputs: {
          validateOnDirty: true,
          required: false,
          label: this.buildTranslationKey('location'),
          displayCharactersCounter: true,
        },
        initialValue: this.subprocessor.location,
      }),
      [FormControls.Usage]: new TextAreaControl({
        initialInputs: {
          validateOnDirty: true,
          required: true,
          label: this.buildTranslationKey('describeSubprocessorUsage'),
          autogrow: true,
          minNumberOfRows: 10,
          resizable: false,
        },
        validators: [Validators.required],
        initialValue: this.subprocessor.usage,
      }),
    });

    this.initialFormValues = this.form.value;
  }

  private handleSidePanelResizing(): void {
    this.resizeObserver = new ResizeObserver(() => this.calculateFormMaxHeight());
    this.resizeObserver.observe(this.elementRef.nativeElement);
  }

  private calculateFormMaxHeight(): void {
    if (!this.contentContainer || !this.subprocessorNameContainer) {
      return;
    }

    const containerComputedStyle = getComputedStyle(this.contentContainer.nativeElement);
    const containerHeight = parseFloat(containerComputedStyle.height);
    const containerGap = parseFloat(containerComputedStyle.gap);

    const nameComputedStyle = getComputedStyle(this.subprocessorNameContainer.nativeElement);
    const nameHeight = parseFloat(nameComputedStyle.height);

    this.formMaxHeight = containerHeight - containerGap - nameHeight;
    this.cd.detectChanges();
  }
}
